import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    List,
    ListItem,
    Collapse,
    IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Field types available for quiz questions
const QuestionTypes = {
    text: 'Texte libre',
    select: 'Liste déroulante',
    radio: 'Choix unique',
    checkbox: 'Choix multiple',
    number: 'Nombre',
    textarea: 'Texte long'
};

function CostumizedQuiz({ onQuestionsChange, initialQuestions = [] }) {
    const [questions, setQuestions] = useState(initialQuestions);
    const [expandedQuestion, setExpandedQuestion] = useState(null);

    // Update parent when questions change
    const updateQuestions = (newQuestions) => {
        setQuestions(newQuestions);
        if (onQuestionsChange) {
            onQuestionsChange(newQuestions);
        }
    };

    // Add a new question
    const addQuestion = (type = 'text') => {
        const newQuestion = {
            id: `question_${Date.now()}`,
            type: type,
            label: `Nouvelle question`,
            options: ['select', 'radio', 'checkbox'].includes(type) ? ['Option 1', 'Option 2'] : undefined,
            required: false
        };
        const newQuestions = [...questions, newQuestion];
        updateQuestions(newQuestions);
        setExpandedQuestion(questions.length);
    };

    // Update a question
    const updateQuestion = (index, updates) => {
        const newQuestions = questions.map((q, i) =>
            i === index ? { ...q, ...updates } : q
        );
        updateQuestions(newQuestions);
    };

    // Remove a question
    const removeQuestion = (index) => {
        const newQuestions = questions.filter((_, i) => i !== index);
        updateQuestions(newQuestions);
        if (expandedQuestion === index) setExpandedQuestion(null);
    };

    // Add an option to a question
    const addOption = (questionIndex) => {
        const question = questions[questionIndex];
        const newOptions = [...(question.options || []), `Option ${(question.options || []).length + 1}`];
        updateQuestion(questionIndex, { options: newOptions });
    };

    // Update an option
    const updateOption = (questionIndex, optionIndex, newValue) => {
        const question = questions[questionIndex];
        const newOptions = question.options.map((opt, idx) => idx === optionIndex ? newValue : opt);
        updateQuestion(questionIndex, { options: newOptions });
    };

    // Remove an option
    const removeOption = (questionIndex, optionIndex) => {
        const question = questions[questionIndex];
        const newOptions = question.options.filter((_, idx) => idx !== optionIndex);
        updateQuestion(questionIndex, { options: newOptions });
    };

    // Handle drag and drop
    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(questions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        updateQuestions(items);
    };

    // Toggle question expansion
    const toggleExpansion = (index) => {
        setExpandedQuestion(expandedQuestion === index ? null : index);
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Questions List */}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="questions">
                    {(provided) => (
                        <List {...provided.droppableProps} ref={provided.innerRef} sx={{ p: 0 }}>
                            {questions.map((question, index) => (
                                <Draggable
                                    key={question.id}
                                    draggableId={question.id}
                                    index={index}
                                >
                                    {(provided) => (
                                        <ListItem
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                mb: 1.5,
                                                p: 0,
                                                borderRadius: 2,
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #e0e0e0',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 1.5 }}>
                                                <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                                                    <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    value={question.label}
                                                    onChange={(e) => updateQuestion(index, { label: e.target.value })}
                                                    variant="outlined"
                                                    size="small"
                                                    placeholder="Intitulé de la question"
                                                    sx={{ mr: 1 }}
                                                />
                                                <Select
                                                    value={question.type}
                                                    onChange={(e) => {
                                                        const newType = e.target.value;
                                                        const updates = { type: newType };
                                                        if (['select', 'radio', 'checkbox'].includes(newType) && !question.options) {
                                                            updates.options = ['Option 1', 'Option 2'];
                                                        }
                                                        updateQuestion(index, updates);
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ width: 140, mr: 0.5, fontSize: '0.8rem' }}
                                                >
                                                    {Object.entries(QuestionTypes).map(([type, label]) => (
                                                        <MenuItem key={type} value={type} sx={{ fontSize: '0.85rem' }}>{label}</MenuItem>
                                                    ))}
                                                </Select>
                                                <IconButton onClick={() => removeQuestion(index)} color="error" size="small">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton onClick={() => toggleExpansion(index)} size="small">
                                                    {expandedQuestion === index ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                </IconButton>
                                            </Box>

                                            {/* Expanded Options */}
                                            <Collapse in={expandedQuestion === index} timeout="auto" unmountOnExit>
                                                <Box sx={{ p: 1.5, pt: 0, borderTop: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={question.required}
                                                                onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                                                                color="primary"
                                                                size="small"
                                                            />
                                                        }
                                                        label={<Typography variant="body2">Obligatoire</Typography>}
                                                        sx={{ mb: 1 }}
                                                    />

                                                    {['select', 'radio', 'checkbox'].includes(question.type) && (
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: '#666' }}>
                                                                Options de réponse
                                                            </Typography>
                                                            {question.options?.map((option, optionIndex) => (
                                                                <Box key={optionIndex} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                                                    <TextField
                                                                        value={option}
                                                                        onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder={`Option ${optionIndex + 1}`}
                                                                    />
                                                                    <IconButton
                                                                        onClick={() => removeOption(index, optionIndex)}
                                                                        disabled={question.options.length <= 1}
                                                                        size="small"
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                            <Button
                                                                startIcon={<AddIcon />}
                                                                onClick={() => addOption(index)}
                                                                size="small"
                                                                sx={{ mt: 0.5, fontSize: '0.75rem' }}
                                                            >
                                                                Ajouter une option
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </ListItem>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </List>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Add Question Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => addQuestion('text')} sx={{ borderRadius: 1, fontSize: '0.75rem', py: 0.5 }}>
                    Texte
                </Button>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => addQuestion('select')} sx={{ borderRadius: 1, fontSize: '0.75rem', py: 0.5 }}>
                    Liste
                </Button>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => addQuestion('radio')} sx={{ borderRadius: 1, fontSize: '0.75rem', py: 0.5 }}>
                    Choix unique
                </Button>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => addQuestion('checkbox')} sx={{ borderRadius: 1, fontSize: '0.75rem', py: 0.5 }}>
                    Choix multiple
                </Button>
            </Box>
        </Box>
    );
}

export default CostumizedQuiz;

